import React, { useCallback, useEffect, useMemo, useState } from "react";
import style from "css/admin/component.module.css";
import { Button, Table, message } from "antd";
import axios from "axios";
import { ReactComponent as PlusIcon } from "asset/plus_icon.svg";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import AdminModal from "component/admin/modal/adminModal";
import AdminDeleteConfirmModal from "component/admin/modal/adminDeleteConfirmModal";

function AdminMgmt() {
  const [init, setInit] = useState(true);

  // 데이터
  const [isLoading, setIsLoading] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [totalCnt, setTotalCnt] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // 등록/수정 모달
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedRecord, setSelectedRecord] = useState(null);

  // 삭제 확인 모달
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 관리자 목록 조회
  const getAdminList = useCallback((page = 1) => {
    setIsLoading(true);
    axios
      .get("/admin", { params: { page, size: pageSize } })
      .then((res) => {
        if (res.status === 200 && res.data) {
          setAdminList(res.data.list || []);
          setTotalCnt(res.data.totalCnt || 0);
          setCurrentPage(res.data.page || 1);
        } else {
          setAdminList([]);
          setTotalCnt(0);
        }
      })
      .catch(() => {
        setAdminList([]);
        setTotalCnt(0);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (init) {
      getAdminList(1);
      setInit(false);
    }
  }, [getAdminList, init]);

  // 등록 모달
  const openCreateModal = useCallback(() => {
    setModalMode("create");
    setSelectedRecord(null);
    setModalOpen(true);
  }, []);

  // 수정 모달
  const openEditModal = useCallback((record) => {
    setModalMode("edit");
    setSelectedRecord(record);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRecord(null);
  }, []);

  // 삭제
  const deleteHandler = useCallback((record) => {
    setDeleteTarget(record || null);
    setDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (deleteLoading) return;
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  }, [deleteLoading]);

  const submitDelete = useCallback(async () => {
    const memberId = deleteTarget?.MEMBER_ID;
    if (!memberId) {
      message.error("삭제할 관리자 ID가 없습니다.");
      return;
    }
    try {
      setDeleteLoading(true);
      await axios.delete(`/admin/${memberId}`);
      message.success("삭제되었습니다.");
      closeDeleteModal();
      getAdminList(currentPage);
    } catch (e) {
      const msg =
        e?.response?.data ?? e?.message ?? "삭제 중 오류가 발생했습니다.";
      message.error(String(msg));
    } finally {
      setDeleteLoading(false);
    }
  }, [closeDeleteModal, currentPage, deleteTarget, getAdminList]);

  // 역할 표시
  const getRoleLabel = (type) => {
    if (type === "CD0101") return "소유자";
    if (type === "CD0102") return "관리자";
    return type || "-";
  };

  const dataSource = useMemo(() => adminList || [], [adminList]);

  const columns = useMemo(
    () => [
      {
        title: "아이디",
        dataIndex: "MEMBER_ID",
        key: "MEMBER_ID",
        render: (text) => <span className={style.table_cell}>{text}</span>,
      },
      {
        title: "이름",
        dataIndex: "MEMBER_NM",
        key: "MEMBER_NM",
        render: (text) => (
          <span className={style.table_cell}>{text || "-"}</span>
        ),
      },
      {
        title: "이메일",
        dataIndex: "EMAIL",
        key: "EMAIL",
        render: (text) => (
          <span className={style.table_cell}>{text || "-"}</span>
        ),
      },
      {
        title: "역할",
        dataIndex: "MEMBER_TYPE",
        key: "MEMBER_TYPE",
        width: 120,
        render: (v) => (
          <span className={style.table_cell}>{getRoleLabel(v)}</span>
        ),
      },
      {
        title: "",
        key: "actions",
        width: 140,
        align: "center",
        render: (_, record) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
            <Button
              className={style.edit_btn}
              type="text"
              icon={<EditOutlined />}
              aria-label="수정"
              title="수정"
              onClick={() => openEditModal(record)}
            />
            <Button
              className={style.delete_btn}
              type="default"
              icon={<DeleteOutlined />}
              aria-label="삭제"
              title="삭제"
              onClick={() => deleteHandler(record)}
            />
          </div>
        ),
      },
    ],
    [deleteHandler, openEditModal],
  );

  return (
    <div className={style.page_wrap}>
      <div className={style.content_wrapper}>
        <div className={style.content_container}>
          <div className={style.header}>
            <div className={style.text}>전체 관리자 목록 (총 {totalCnt}명)</div>
            <div className={style.buttons}>
              <Button className={style.register_btn} onClick={openCreateModal}>
                <PlusIcon width={20} height={20} />
                관리자 생성
              </Button>
            </div>
          </div>
          <div className={style.content}>
            <div className={style.table_section}>
              <Table
                rowKey={(row) => row?.MEMBER_ID}
                columns={columns}
                dataSource={dataSource}
                size="middle"
                loading={isLoading}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalCnt,
                  showSizeChanger: false,
                  onChange: (page) => getAdminList(page),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <AdminModal
        isOpen={modalOpen}
        close={closeModal}
        mode={modalMode}
        initialData={selectedRecord}
        onSuccess={() => getAdminList(currentPage)}
      />

      <AdminDeleteConfirmModal
        open={deleteModalOpen}
        onCancel={closeDeleteModal}
        onConfirm={submitDelete}
        loading={deleteLoading}
        record={deleteTarget}
      />
    </div>
  );
}

export default AdminMgmt;
